import { useFetchNaviSamplesApi, usePutNaviSampleResultConfirmApi, usePutNaviSampleResultStatusApi } from 'api/navi_sample'
import { ContentBody, ContentHeader } from 'components/shared/content'
import {
    CheckboxTableRow,
    ExTable,
    FixedFooter,
    OrderTableCell,
    TableBody,
    TableCell,
    TableHead,
    TableWrapper,
    useMultipleCheckForm,
} from 'components/shared/widget'
import * as H from 'history'
import { useContext, useEffect, useState } from 'react'
import { Badge, Button, ButtonGroup, ToggleButton } from 'react-bootstrap'
import { withRouter } from 'react-router-dom'
import { useEffectSkipFirst, useForm, useQuery } from 'utils/hooks'
import dayjs from 'dayjs'
import { NaviSample, NaviSampleForm, NaviSampleSearchForm } from 'entities/navi_sample'
import { GlobalStateContext } from 'contexts/global_state_context'
import { CheckboxField, InputField } from 'components/shared/input'
import { StatusChoices } from '@craif/product.entities.navi_sample'
import { Pagination } from 'components/shared/pagination'
import { toDateText } from 'utils/util'

type Props = {
    history: H.History
}

export const NaviResultsPage = (props: Props) => {
    const params = useQuery<NaviSampleSearchForm>()
    const multipleCheckForm = useMultipleCheckForm()
    const bulkUpdateForm = useForm<NaviSampleForm[]>([])
    const naviSampleResultStatusApi = usePutNaviSampleResultStatusApi()
    const globalState = useContext(GlobalStateContext)
    const [currentState, setCurrentState] = useState<'APPROVED' | 'SENT'>('APPROVED')
    const sampleSearchForm = useForm<NaviSampleSearchForm>({
        ...params,
        status: currentState,
        isInterviewed: true,
        ordering: 'navi_sample_return_deadline,navi_sample_id',
        request_form__is_result_confirm_required: false,
    })
    const confirmSampleSearchForm = useForm<NaviSampleSearchForm>({
        ...params,
        status: currentState,
        isInterviewed: true,
        ordering: 'navi_sample_return_deadline,navi_sample_id',
        request_form__is_result_confirm_required: true,
    })
    const samplesApi = useFetchNaviSamplesApi(sampleSearchForm)
    const confirmSampleApi = useFetchNaviSamplesApi(confirmSampleSearchForm)
    const [isConfirmMode, setIsConfirmMode] = useState<boolean>(false)
    const [naviSamples, setNaviSamples] = useState<NaviSample[]>([])

    const sampleResultConfirmApi = usePutNaviSampleResultConfirmApi()

    const fetchSamples = () => {
        confirmSampleApi.execute()
        samplesApi.execute()
    }

    useEffect(() => {
        fetchSamples()
    }, [])

    useEffect(() => {
        if ((samplesApi.isSuccess() && !isConfirmMode) || (confirmSampleApi.isSuccess() && isConfirmMode)) {
            multipleCheckForm.reset()

            const samples = isConfirmMode ? confirmSampleApi.response.results : samplesApi.response.results

            const deepCopiedSamples = JSON.parse(
                JSON.stringify(
                    samples.map((s) => {
                        return {
                            id: s.id,
                            isResultChecked: s.isResultChecked || false,
                            code: s.code,
                            resultCheckedUserId: s.resultCheckedUserId || undefined,
                            resultCheckedUser: s.resultCheckedUser,
                            requestForm: s.requestForm,
                            sentAt: s.sentAt,
                            isRecollection: s.isRecollection,
                            returnDeadline: s.returnDeadline,
                        }
                    })
                )
            )
            bulkUpdateForm.set(deepCopiedSamples)
            setNaviSamples(deepCopiedSamples)
        }
    }, [samplesApi.loading, confirmSampleApi.loading, isConfirmMode])

    useEffect(() => {
        sampleSearchForm.update((f) => {
            f.status = currentState as StatusChoices
        })
    }, [currentState])

    useEffect(() => {
        globalState.setLoading(naviSampleResultStatusApi.loading)
        if (naviSampleResultStatusApi.isSuccess()) {
            fetchSamples()
        }
    }, [naviSampleResultStatusApi.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(sampleResultConfirmApi.loading)
        if (sampleResultConfirmApi.isSuccess()) {
            fetchSamples()
        }
    }, [sampleResultConfirmApi.loading])

    return (
        <>
            <ContentHeader
                headerTitle="ナビ・結果表印刷"
                left={
                    <>
                        <ButtonGroup>
                            <ToggleButton
                                id={`radio-approved`}
                                type="radio"
                                variant={'outline-primary'}
                                name="radio"
                                value={'APPROVED'}
                                checked={currentState === 'APPROVED'}
                                onChange={(e) => setCurrentState('APPROVED')}
                            >
                                未発送
                            </ToggleButton>
                            <ToggleButton
                                id={`radio-sent`}
                                type="radio"
                                variant={'outline-primary'}
                                name="radio"
                                value={'SENT'}
                                checked={currentState === 'SENT'}
                                onChange={(e) => setCurrentState('SENT')}
                            >
                                発送済
                            </ToggleButton>
                        </ButtonGroup>
                        <Button
                            variant="link"
                            onClick={() => {
                                setIsConfirmMode(!isConfirmMode)
                            }}
                        >
                            {isConfirmMode
                                ? '結果表印刷一覧に戻る'
                                : `管理者確認待ちの重要結果表 ${
                                      confirmSampleApi.response.results.filter((s) => s.requestForm?.isResultConfirmRequired).length
                                  }件`}
                        </Button>
                    </>
                }
                right={
                    <>
                        <Button
                            onClick={() => {
                                if (isConfirmMode) {
                                    sampleResultConfirmApi.execute(bulkUpdateForm)
                                } else {
                                    naviSampleResultStatusApi.execute(bulkUpdateForm)
                                }
                            }}
                        >
                            保存
                        </Button>
                    </>
                }
            />

            <Pagination pageSet={samplesApi.pageSet} response={samplesApi.response} searchForm={sampleSearchForm}></Pagination>
            <ContentBody>
                <TableWrapper showFooter={multipleCheckForm.form.object.items.length > 0} contentHeaderCount={2}>
                    <ExTable className="sticky-table">
                        <TableHead>
                            <CheckboxTableRow multipleCheckForm={multipleCheckForm} allCheck allItems={samplesApi.response.results} head>
                                <TableCell th width={100}>
                                    ID
                                </TableCell>
                                <TableCell th minWidth={200}>
                                    コード
                                </TableCell>
                                <TableCell th width={150}>
                                    発送期限
                                </TableCell>
                                <OrderTableCell th width={150} form={sampleSearchForm} searchType={{ name: 'sentAt', type: 'date' }}>
                                    発送日
                                </OrderTableCell>
                                <TableCell th width={150}>
                                    再検査
                                </TableCell>
                                <TableCell th width={150}>
                                    結果表リンク
                                </TableCell>
                                {isConfirmMode ? (
                                    <TableCell th width={150}>
                                        管理者結果表内容確認
                                    </TableCell>
                                ) : (
                                    <>
                                        <TableCell th width={150}>
                                            結果表内容確認
                                        </TableCell>
                                        <TableCell th width={150}>
                                            結果表確認担当者
                                        </TableCell>
                                    </>
                                )}
                                <TableCell th width={200}>
                                    確認コード
                                </TableCell>
                            </CheckboxTableRow>
                        </TableHead>
                        <TableBody loading={samplesApi.loading}>
                            {naviSamples?.map((naviSample, index) => {
                                const remainingDays = dayjs(naviSample.returnDeadline!).diff(dayjs(), 'day')
                                return (
                                    <CheckboxTableRow key={naviSample.id} multipleCheckForm={multipleCheckForm} item={naviSample}>
                                        <TableCell>{naviSample.id}</TableCell>
                                        <TableCell>{naviSample.code}</TableCell>
                                        <TableCell>
                                            {remainingDays > 0 ? <Badge bg="success">{remainingDays}日</Badge> : <Badge bg={'danger'}>{remainingDays}日</Badge>}
                                        </TableCell>
                                        <TableCell>{toDateText(naviSample.sentAt)}</TableCell>
                                        <TableCell>{naviSample.isRecollection ? <Badge bg="danger">再検査</Badge> : '-'}</TableCell>
                                        <TableCell>
                                            <a href={`/navi/results/preview?id=${naviSample.id}`} target="_blank" rel="noreferrer">
                                                結果表リンク
                                            </a>
                                        </TableCell>
                                        {isConfirmMode ? (
                                            <TableCell
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                }}
                                            >
                                                {/* memo: isResultConfirmRequiredがfalseの場合、確認済になる */}
                                                <CheckboxField
                                                    form={bulkUpdateForm}
                                                    attr={[index, 'requestForm', 'isResultConfirmRequired']}
                                                    checkboxLabel="確認済"
                                                    value={!bulkUpdateForm.object[index].requestForm?.isResultConfirmRequired}
                                                    onChange={(e) => {
                                                        bulkUpdateForm.updateObject([index, 'requestForm', 'isResultConfirmRequired'], !e?.target.checked)
                                                    }}
                                                />
                                            </TableCell>
                                        ) : (
                                            <>
                                                <TableCell onClick={(e) => e.stopPropagation()}>
                                                    <CheckboxField
                                                        form={bulkUpdateForm}
                                                        attr={[index, 'isResultChecked']}
                                                        checkboxLabel="確認済"
                                                        disabled={naviSample.isResultChecked}
                                                        onChange={(e) => {
                                                            if (e?.target.checked) {
                                                                bulkUpdateForm.update((f) => {
                                                                    f[index].resultCheckedUserId = globalState.user?.id
                                                                })
                                                            } else {
                                                                bulkUpdateForm.update((f) => {
                                                                    f[index].resultCheckedUserId = undefined
                                                                    f[index].status = undefined
                                                                })
                                                            }
                                                        }}
                                                    ></CheckboxField>
                                                </TableCell>
                                                <TableCell>
                                                    {naviSample.resultCheckedUser?.firstName} {naviSample.resultCheckedUser?.lastName}
                                                </TableCell>
                                            </>
                                        )}

                                        <TableCell
                                            onClick={(e) => {
                                                e.stopPropagation()
                                            }}
                                        >
                                            <div style={{ position: 'relative' }}>
                                                <InputField
                                                    form={bulkUpdateForm}
                                                    attr={[index, 'trackNumber']}
                                                    style={{ margin: 0 }}
                                                    disabled={naviSample.status === 'SENT'}
                                                    onChange={(e) => {
                                                        if (e?.target.value === naviSample.code) {
                                                            bulkUpdateForm.update((f) => {
                                                                f[index].status = 'SENT'
                                                                f[index].sentAt = new Date().toISOString()
                                                            })
                                                        }
                                                    }}
                                                />
                                                {naviSample.status === 'APPROVED' &&
                                                    bulkUpdateForm.object[index] &&
                                                    bulkUpdateForm.object[index].trackNumber &&
                                                    naviSample.code !== bulkUpdateForm.object[index].trackNumber && (
                                                        <div style={{ fontSize: 10, color: 'red', position: 'absolute', bottom: -15, left: 4 }}>
                                                            正しいコードをスキャンしてください
                                                        </div>
                                                    )}
                                            </div>
                                        </TableCell>
                                    </CheckboxTableRow>
                                )
                            })}
                        </TableBody>
                    </ExTable>

                    <Pagination pageSet={samplesApi.pageSet} response={samplesApi.response} searchForm={sampleSearchForm}></Pagination>
                </TableWrapper>
            </ContentBody>

            <FixedFooter open={multipleCheckForm.form.object.items.length > 0}>
                <Button
                    variant="secondary"
                    onClick={() => {
                        window.open('/navi/results/postage_slips?id=' + multipleCheckForm.ids().join(','), '_blank')
                    }}
                >
                    後納郵便差出票印刷
                </Button>
                <Button
                    variant="secondary"
                    onClick={() => {
                        window.open('/navi/results/preview?id=' + multipleCheckForm.ids().join(','), '_blank')
                    }}
                >
                    結果表印刷
                </Button>
            </FixedFooter>
        </>
    )
}

export default withRouter(NaviResultsPage)
